import * as XLSX from "xlsx";
import { usesServerNormalizer, type AttachmentKind } from "./documentFormats.ts";

export { recognizeAttachment, type AttachmentKind } from "./documentFormats.ts";
export type NormalizedAttachment = { path: string; name: string; kind: AttachmentKind; content: string | null };

export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_EXTRACTED_CHARACTERS = 30_000;
const MAX_ARCHIVE_BYTES = 40 * 1024 * 1024;
const MAX_ARCHIVE_ENTRIES = 2_000;
const MAX_SHEETS = 20;
const MAX_ROWS_PER_SHEET = 500;
const MAX_COLUMNS_PER_SHEET = 50;

export function validateArchiveBounds(bytes: Uint8Array): void {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let end = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65_557); i--) {
    if (view.getUint32(i, true) === 0x06054b50) { end = i; break; }
  }
  if (end < 0) throw new AttachmentExtractionError("This archive is damaged or unsupported.");
  const entries = view.getUint16(end + 10, true);
  if (entries === 0xffff || entries > MAX_ARCHIVE_ENTRIES) throw new AttachmentExtractionError("This archive has too many parts.");
  let cursor = view.getUint32(end + 16, true);
  let expanded = 0;
  for (let i = 0; i < entries; i++) {
    if (cursor + 46 > bytes.length || view.getUint32(cursor, true) !== 0x02014b50) throw new AttachmentExtractionError("This archive is damaged.");
    const flags = view.getUint16(cursor + 8, true);
    const method = view.getUint16(cursor + 10, true);
    const uncompressed = view.getUint32(cursor + 24, true);
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    if (flags & 1) throw new AttachmentExtractionError("This archive is encrypted.");
    if (method !== 0 && method !== 8) throw new AttachmentExtractionError("This archive uses unsupported compression.");
    if (uncompressed === 0xffffffff) throw new AttachmentExtractionError("This archive exceeds the safe limit.");
    expanded += uncompressed;
    if (expanded > MAX_ARCHIVE_BYTES) throw new AttachmentExtractionError("This archive expands beyond the safe limit.");
    cursor += 46 + nameLength + extraLength + commentLength;
    if (cursor > bytes.length) throw new AttachmentExtractionError("This archive is damaged.");
  }
}

export class AttachmentExtractionError extends Error {}

function bounded(text: string): string {
  return text.length > MAX_EXTRACTED_CHARACTERS
    ? `${text.slice(0, MAX_EXTRACTED_CHARACTERS)}\n\n[Attachment content truncated]`
    : text;
}

function xmlText(text: string): string {
  return text.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'").replace(/&amp;/g, "&");
}

// Read only the required Office XML entry. The central-directory bounds reject zip bombs
// before decompression; neither macros nor external relationships are inspected.
async function zipEntry(bytes: Uint8Array, target: string): Promise<string> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let end = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65_557); i--) {
    if (view.getUint32(i, true) === 0x06054b50) { end = i; break; }
  }
  if (end < 0) throw new AttachmentExtractionError("This Office file is not a valid ZIP document.");
  const entries = view.getUint16(end + 10, true);
  if (entries > MAX_ARCHIVE_ENTRIES) throw new AttachmentExtractionError("This Office file has too many parts.");
  let cursor = view.getUint32(end + 16, true);
  let total = 0;
  let selected: { method: number; compressed: number; uncompressed: number; offset: number } | null = null;
  for (let i = 0; i < entries; i++) {
    if (cursor + 46 > bytes.length || view.getUint32(cursor, true) !== 0x02014b50) throw new AttachmentExtractionError("This Office file is damaged.");
    const method = view.getUint16(cursor + 10, true);
    const compressed = view.getUint32(cursor + 20, true);
    const uncompressed = view.getUint32(cursor + 24, true);
    const nameLength = view.getUint16(cursor + 28, true);
    const extraLength = view.getUint16(cursor + 30, true);
    const commentLength = view.getUint16(cursor + 32, true);
    const name = new TextDecoder().decode(bytes.subarray(cursor + 46, cursor + 46 + nameLength));
    if (cursor + 46 + nameLength + extraLength + commentLength > bytes.length) throw new AttachmentExtractionError("This Office file is damaged.");
    total += uncompressed;
    if (total > MAX_ARCHIVE_BYTES) throw new AttachmentExtractionError("This Office file expands beyond the safe limit.");
    if (name === target) selected = { method, compressed, uncompressed, offset: view.getUint32(cursor + 42, true) };
    cursor += 46 + nameLength + extraLength + commentLength;
  }
  if (!selected) throw new AttachmentExtractionError("This Office file is missing its document content.");
  const { method, compressed, uncompressed, offset } = selected;
  if (offset + 30 > bytes.length || view.getUint32(offset, true) !== 0x04034b50) throw new AttachmentExtractionError("This Office file is damaged.");
  const start = offset + 30 + view.getUint16(offset + 26, true) + view.getUint16(offset + 28, true);
  if (start + compressed > bytes.length) throw new AttachmentExtractionError("This Office file is damaged.");
  const part = bytes.subarray(start, start + compressed);
  let output: Uint8Array;
  if (method === 0) output = part;
  else if (method === 8) {
    const stream = new Blob([part as BlobPart]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
    const reader = stream.getReader();
    const chunks: Uint8Array[] = [];
    let length = 0;
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        length += value.length;
        if (length > uncompressed || length > MAX_ARCHIVE_BYTES) throw new AttachmentExtractionError("This Office file expands beyond the safe limit.");
        chunks.push(value);
      }
    } finally {
      reader.releaseLock();
    }
    output = new Uint8Array(length);
    let position = 0;
    for (const chunk of chunks) { output.set(chunk, position); position += chunk.length; }
  } else throw new AttachmentExtractionError("This Office file uses unsupported compression.");
  if (output.length !== uncompressed) throw new AttachmentExtractionError("This Office file is damaged.");
  return new TextDecoder("utf-8", { fatal: true }).decode(output);
}

function docxMarkdown(xml: string): string {
  const blocks: string[] = [];
  // WordprocessingML content stays in document order. The parser only reads text,
  // paragraph styles, list markers, and table cells; it never resolves references.
  const tableOrParagraph = /<w:tbl(?:\s[^>]*)?>[\s\S]*?<\/w:tbl>|<w:p(?:\s[^>]*)?>[\s\S]*?<\/w:p>/g;
  const paragraph = (part: string): string => {
    const text = [...part.matchAll(/<w:t(?:\s[^>]*)?>([\s\S]*?)<\/w:t>/g)].map((m) => xmlText(m[1])).join("").trim();
    if (!text) return "";
    const heading = part.match(/<w:pStyle[^>]*w:val="Heading([1-6])"/i)?.[1];
    if (heading) return `${"#".repeat(Number(heading))} ${text}`;
    return /<w:numPr\b/.test(part) ? `- ${text}` : text;
  };
  for (const match of xml.matchAll(tableOrParagraph)) {
    const part = match[0];
    if (part.startsWith("<w:tbl")) {
      const rows = [...part.matchAll(/<w:tr(?:\s[^>]*)?>([\s\S]*?)<\/w:tr>/g)].slice(0, MAX_ROWS_PER_SHEET);
      for (const row of rows) {
        const cells = [...row[1].matchAll(/<w:tc(?:\s[^>]*)?>([\s\S]*?)<\/w:tc>/g)]
          .slice(0, MAX_COLUMNS_PER_SHEET).map((cell) => paragraph(cell[1]).replace(/\|/g, "\\|"));
        blocks.push(`| ${cells.join(" | ")} |`);
      }
    } else {
      const text = paragraph(part);
      if (text) blocks.push(text);
    }
    if (blocks.join("\n").length > MAX_EXTRACTED_CHARACTERS) break;
  }
  return bounded(blocks.join("\n\n"));
}

function xlsxMarkdown(bytes: Uint8Array): string {
  const workbook = XLSX.read(bytes, { type: "array", cellFormula: true, cellText: true, sheetRows: MAX_ROWS_PER_SHEET + 1 });
  const sections: string[] = [];
  for (const name of workbook.SheetNames.slice(0, MAX_SHEETS)) {
    const sheet = workbook.Sheets[name];
    const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
    const lines = [`## Sheet: ${name}`];
    const lastRow = Math.min(range.e.r, range.s.r + MAX_ROWS_PER_SHEET - 1);
    const lastCol = Math.min(range.e.c, range.s.c + MAX_COLUMNS_PER_SHEET - 1);
    for (let row = range.s.r; row <= lastRow; row++) {
      const cells: string[] = [];
      for (let col = range.s.c; col <= lastCol; col++) {
        const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })];
        const value = cell ? String(cell.w ?? cell.v ?? "") : "";
        cells.push((cell?.f ? `${value} (=${cell.f})` : value).replace(/\|/g, "\\|").replace(/[\r\n]+/g, " "));
      }
      lines.push(`| ${cells.join(" | ")} |`);
      if (sections.join("\n").length + lines.join("\n").length > MAX_EXTRACTED_CHARACTERS) break;
    }
    if (range.e.r > lastRow || range.e.c > lastCol) lines.push("[Sheet truncated]");
    sections.push(lines.join("\n"));
    if (sections.join("\n").length > MAX_EXTRACTED_CHARACTERS) break;
  }
  if (workbook.SheetNames.length > MAX_SHEETS) sections.push("[Additional sheets omitted]");
  return bounded(sections.join("\n\n"));
}

export async function normalizeAttachment(bytes: Uint8Array, kind: AttachmentKind): Promise<string | null> {
  if (!bytes.length || bytes.length > MAX_ATTACHMENT_BYTES) throw new AttachmentExtractionError("The attachment exceeds the 10 MB file limit.");
  if (kind === "pdf" || kind === "image") return null;
  try {
    if (usesServerNormalizer(kind)) throw new AttachmentExtractionError("This document requires server extraction.");
    if (kind === "docx") {
      const content = docxMarkdown(await zipEntry(bytes, "word/document.xml"));
      if (!content.trim()) throw new AttachmentExtractionError("This document has no readable text.");
      return content;
    }
    if (kind === "xlsx") {
      await zipEntry(bytes, "xl/workbook.xml"); // archive preflight before SheetJS expands it
      const content = xlsxMarkdown(bytes);
      if (!content.trim()) throw new AttachmentExtractionError("This workbook has no readable cells.");
      return content;
    }
    const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    if (!text.trim()) throw new AttachmentExtractionError("This file has no readable text.");
    return bounded(kind === "csv" ? `## CSV\n\n${text}` : text);
  } catch (error) {
    if (error instanceof AttachmentExtractionError) throw error;
    throw new AttachmentExtractionError("The attachment could not be read. Check the file and try again.");
  }
}
